import { Enums } from '@bode-canada/core';
import NumberFormat, { CurrencyCode } from '@/ui-kit/helpers/NumberFormatHelper';
import { UNAVAILABLE_MESSAGE } from '../../constants';

export default class ListingAccordionHelper {
  static getFullListingAccordions(listing) {
    return this.#filterItems([
      this.#getHighlightsAccordion(listing),
      ...this.getShortListingAccordions(listing),
    ]);
  }

  static getShortListingAccordions(listing) {
    const isNotVacantLand = listing.buildingType !== Enums.PropertyTypeEnum.VACANT_LAND
      && listing.buildingClass !== Enums.PropertyTypeEnum.VACANT_LAND;

    return this.#filterItems([
      this.#getDescriptionAccordion(listing),
      this.#getInteriorDetails(listing, isNotVacantLand),
      this.#getExteriorDetails(listing, isNotVacantLand),
      this.#getRoomsDimensions(listing, isNotVacantLand),
      this.#getMoreInformation(listing, isNotVacantLand),
    ]);
  }

  static #getDescriptionAccordion(listing) {
    return {
      title: 'Description',
      isExpanded: true,
      description: listing.description,
    };
  }

  static #getHighlightsAccordion(listing) {
    return {
      title: 'Owner\'s Highlights',
      isExpanded: true,
      description: listing.ownersHighlight,
    };
  }

  static #getInteriorDetails(listing, isNotVacantLand) {
    return isNotVacantLand ? {
      title: 'Interior Details',
      isExpanded: false,
      characteristicSections: [
        {
          value: [
            {
              title: 'Interior Features',
              value: this.#stringifyArray(listing.interiorFeatures),
            },
            {
              title: 'Flooring',
              value: this.#stringifyArray(listing.flooring),
            },
            {
              title: 'Cooling',
              value: this.#stringifyArray(listing.cooling),
            },
            {
              title: 'Basement details',
              value: this.#stringifyArray(listing.basementFeatures),
            },
            {
              title: 'Basement features',
              value: this.#stringifyArray(listing.basementDevFeatures),
            },
            {
              title: 'Suite',
              value: listing.legalSuite,
            },
            {
              title: 'Goods included (appliances)',
              value: this.#stringifyArray(listing.appliancesIncluded),
            },
            {
              title: 'Goods included (other equipment)',
              value: this.#stringifyArray(listing.otherEquipmentIncluded),
            },
            {
              title: 'Goods included (other unattached items)',
              value: listing.otherUnattachedGoodsIncluded,
            },
          ].filter(this.#doesValueExist),
        },
      ],
    } : {};
  }

  static #getExteriorDetails(listing, isNotVacantLand) {
    return isNotVacantLand ? {
      title: 'Exterior Details',
      isExpanded: false,
      characteristicSections: [
        {
          value: [
            {
              title: 'Exterior',
              value: this.#stringifyArray(listing.exterior),
            },
            {
              title: 'Number of finished levels',
              value: listing.finishedLevels,
            },
            {
              title: 'Exterior features',
              value: this.#stringifyArray(listing.exteriorFeatures),
            },
            {
              title: 'Construction type',
              value: listing.constructionType,
            },
            {
              title: 'Roof type',
              value: listing.roofType,
            },
            {
              title: 'Foundation type',
              value: listing.foundation,
            },
          ].filter(this.#doesValueExist),
        },
      ],
    } : {};
  }

  static #getMoreInformation(listing, isNotVacantLand) {
    return {
      title: 'More Information',
      isExpanded: false,
      characteristicSections: [
        isNotVacantLand ? {
          title: 'Property',
          value: [
            {
              title: 'Community features',
              value: this.#stringifyArray(listing.communityFeatures),
            },
            {
              title: 'Lot features',
              value: this.#stringifyArray(listing.lotFeatures),
            },
            {
              title: 'Front exposure',
              value: listing.frontExposure,
            },
            {
              title: 'Multi-unit property?',
              value: this.#stringifyBoolean(listing.multiUnit),
            },
            {
              title: 'Number of legal units for sale',
              value: listing.multiUnit && listing.availableUnits,
            },
            {
              title: 'HOA fee required?',
              value: this.#stringifyBoolean(listing.hoaFeeRequired),
            },
            {
              title: 'HOA fee',
              value: this.#formatMoney(
                listing.hoaFeeAmount,
                ` / ${listing.hoaFeeSchedule === Enums.HOAFeeSchedule.ANNUAL ? 'year' : 'month'}`,
              ),
            },
            {
              title: 'HOA fee includes',
              value: this.#stringifyArray(listing.hoaFeeIncludes),
            },
          ].filter(this.#doesValueExist),
        } : {},
        listing.isCondo ? {
          title: 'Condo Details',
          value: [
            {
              title: 'Condo type',
              value: listing.condoType,
            },
            {
              title: 'Condo fee',
              value: this.#formatMoney(listing.condoFee, ' / month'),
            },
            {
              title: 'Condo fee includes',
              value: this.#stringifyArray(listing.condoFeeIncludes),
            },
            {
              title: 'Animal Policy',
              value: this.#isBoolean(listing.isPetFriendly) && (listing.isPetFriendly
                ? `Allows pets (${listing.petRestrictions || 'all'})`
                : 'No pets'),
            },
            {
              title: 'Number of legal units for sale',
              value: listing.multiUnit && listing.availableUnits,
            },
          ].filter(this.#doesValueExist),
        } : {},
        isNotVacantLand ? {
          title: 'Parking',
          value: [
            {
              title: 'Parking space included',
              value: this.#stringifyBoolean(listing.parkingSpace),
            },
            {
              title: 'Total parking',
              value: listing.totalParking,
            },
            {
              title: 'Parking features',
              value: this.#stringifyArray(listing.parkingFeatures),
            },
          ].filter(this.#doesValueExist),
        } : {},
        isNotVacantLand ? {
          title: 'Utilities',
          value: [
            {
              title: 'Water supply',
              value: this.#stringifyArray(listing.waterSupply),
            },
          ].filter(this.#doesValueExist),
        } : {},
      ].filter(this.#doesValueExist),
    };
  }

  static #getRoomsDimensions(listing, isNotVacantLand) {
    return isNotVacantLand ? {
      title: 'Rooms Dimensions',
      isExpanded: false,
      characteristicSections: listing.rooms && !listing.isBode && Object.entries(listing.rooms)
        .map(([floorName, rooms]) => ({
          title: floorName,
          value: rooms
            .map(({ type, length, width }) => ({
              title: type,
              value: length
                && width
                && `${this.#formatDimension(length)} m x ${this.#formatDimension(width)} m`,
            }))
            .filter(this.#doesValueExist),
        }))
        .filter(this.#doesValueExist),
    } : {};
  }

  static #stringifyArray(input) {
    if (this.#isString(input)) {
      return input;
    }

    if (this.#isArray(input) && !!input.length) {
      return input?.join(', ');
    }

    return '';
  }

  static #stringifyBoolean(input) {
    if (!this.#isBoolean(input)) {
      return UNAVAILABLE_MESSAGE;
    }

    return input ? 'Yes' : 'No';
  }

  static #isBoolean(input) {
    return typeof input === 'boolean';
  }

  static #isString(input) {
    return typeof input === 'string';
  }

  static #isArray(input) {
    return Array.isArray(input);
  }

  static #formatMoney(input, postfix = '') {
    return input && `${NumberFormat.formatCurrency(
      input,
      CurrencyCode.CanadianDollars,
    )}${postfix}`;
  }

  static #formatDimension(input) {
    return NumberFormat.format(input, { maximumFractionDigits: 2 });
  }

  static #filterItems(configurations) {
    return configurations.filter(({ description, characteristicSections }) => (
      description || (characteristicSections && characteristicSections.some(this.#doesValueExist))
    ));
  }

  static #doesValueExist({ value }) {
    return !!value && (value.length || value.toString());
  }
}
